import React from 'react';
import { Box, Typography, Container, Card, CardContent, CardMedia, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';

const SectionContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '80px 0',
  backgroundColor: '#ffffff'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '36px',
  fontWeight: 700,
  color: '#121927',
  textAlign: 'center',
  marginBottom: '16px'
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  color: '#666',
  textAlign: 'center',
  marginBottom: '48px',
  maxWidth: '600px',
  margin: '0 auto 48px'
}));

const PostsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
  gap: '24px',
  [theme.breakpoints?.down?.('sm') || '@media (max-width: 600px)']: {
    gridTemplateColumns: '1fr'
  }
}));

const PostCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
  }
}));

const PostImage = styled(CardMedia)(({ theme }) => ({
  height: 200,
  backgroundSize: 'cover',
  backgroundPosition: 'center'
}));

const PostContent = styled(CardContent)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '20px'
}));

const PostType = styled(Chip)(({ theme }) => ({
  alignSelf: 'flex-start',
  marginBottom: '12px',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase'
}));

const PostSummary = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  lineHeight: 1.6,
  color: '#333',
  flex: 1,
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
}));

const PostDate = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  color: '#888',
  marginTop: '16px'
}));

const CTAButton = styled('a')(({ theme }) => ({
  display: 'inline-block',
  marginTop: '12px',
  padding: '8px 16px',
  backgroundColor: '#4285f4',
  color: '#fff',
  borderRadius: '6px',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: 500,
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#3367d6'
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '60px 20px',
  backgroundColor: '#f8f9fa',
  borderRadius: '12px'
}));

const getPostTypeColor = (type) => {
  switch (type) {
    case 'EVENT':
      return 'secondary';
    case 'OFFER':
      return 'error';
    case 'PRODUCT':
      return 'success';
    default:
      return 'primary';
  }
};

const getPostTypeLabel = (type) => {
  switch (type) {
    case 'EVENT':
      return 'Event';
    case 'OFFER':
      return 'Offer';
    case 'PRODUCT':
      return 'Product';
    case 'STANDARD':
      return 'Update';
    default:
      return 'Post';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return '';
  }
};

const getCTALabel = (actionType) => {
  switch (actionType) {
    case 'LEARN_MORE':
      return 'Learn More';
    case 'CALL':
      return 'Call Now';
    case 'BOOK':
      return 'Book Now';
    case 'ORDER':
      return 'Order Online';
    case 'SHOP':
      return 'Shop Now';
    case 'SIGN_UP':
      return 'Sign Up';
    default:
      return 'Learn More';
  }
};

const GMBFeedSection = ({ gmbFeed }) => {
  const {
    title = "Latest Updates",
    subtitle = "Stay updated with our latest news and offers",
    posts = [],
    showSection = true
  } = gmbFeed || {};

  // Don't render if no posts or section disabled
  if (!showSection || !posts || posts.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <Container maxWidth="lg">
        <SectionTitle>{title}</SectionTitle>
        <SectionSubtitle>{subtitle}</SectionSubtitle>
        
        <PostsGrid>
          {posts.slice(0, 6).map((post, index) => {
            const imageUrl = post.media?.[0]?.googleUrl || post.media?.[0]?.sourceUrl || null;
            const hasImage = !!imageUrl;

            return (
              <PostCard key={post.id || index}>
                {hasImage && (
                  <PostImage
                    image={imageUrl}
                    title={`Post ${index + 1}`}
                  />
                )}
                <PostContent>
                  <PostType
                    label={getPostTypeLabel(post.topicType)}
                    color={getPostTypeColor(post.topicType)}
                    size="small"
                  />
                  <PostSummary>
                    {post.summary}
                  </PostSummary>
                  
                  {post.callToAction && post.callToAction.url && (
                    <CTAButton
                      href={post.callToAction.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getCTALabel(post.callToAction.actionType)}
                    </CTAButton>
                  )}
                  
                  <PostDate>
                    {formatDate(post.createTime)}
                  </PostDate>
                </PostContent>
              </PostCard>
            );
          })}
        </PostsGrid>
      </Container>
    </SectionContainer>
  );
};

export default GMBFeedSection;
